import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { captureEvent } from '../lib/analytics/posthog';
import { useAuth } from '../contexts/AuthContext';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Textarea } from '../components/ui/textarea';
import { CheckCircle, ChevronDown, ChevronLeft, ChevronRight, Copy, Loader2, Plus, Send, Trash2, UploadCloud, UserPlus } from 'lucide-react';
import { cn } from '../lib/utils';

interface DocumentInfo {
  title: string;
  description: string;
  expireDate: string;
  summary: string;
  /** Internal reference sent as operationId (GetDocuments filters). */
  operationId: string;
}

interface SignerParty {
  name: string;
  surname: string;
  tin: string;
  fin: string;
  phone: string | null;
  email: string;
  signerGUID: string;
  signerType?: number;
}

/** Matches backend Mysign.Signer.Domain.Entities.SignerType */
const SIGNER_TYPE_SIGNER = 1;
const SIGNER_TYPE_VISA = 2;

interface Signer {
  id: string;
  name: string;
  surname: string;
  email: string;
  phone: string | null;
  fin: string;
  tinCode: string;
  signerType: typeof SIGNER_TYPE_SIGNER | typeof SIGNER_TYPE_VISA;
}

/** Matches UploadDocumentResponseDTO (camelCase JSON). */
interface UploadResponse {
  dataFileGuid: string;
  message?: string;
  signParties?: SignerParty[];
}

interface UploadAndSubmitInfo {
  fileName: string;
  title: string;
  description: string;
  expireDate: string;
  summary: string;
  operationId?: string;
  /** Sent to API without client-only `id` fields. */ 
  signers: Omit<Signer, 'id'>[];
  organisationId?: number;
}

interface Organisation {
  id: string;
  name: string;
  tin: string;
  endpoint: string;
  address: string;
  apiKey?: string;
  description?: string;
}

// Default expiry: next year today (YYYY-MM-DD for date input)
const getDefaultExpireDate = (): string => {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d.toISOString().slice(0, 10);
};

// Steps will be translated in the component
const createEmptySigner = (): Signer => ({
  id: Date.now().toString(),
  name: '',
  surname: '',
  email: '',
  phone: '',
  fin: '',
  tinCode: '',
  signerType: SIGNER_TYPE_SIGNER,
});

const normalizeEmail = (email: string) => email.trim().toLowerCase();

const isPdfFile = (file: File): boolean => {
  const hasPdfExtension = file.name.trim().toLowerCase().endsWith('.pdf');
  const mime = file.type.toLowerCase();
  const hasPdfMime = mime === '' || mime === 'application/pdf' || mime === 'application/octet-stream';
  return hasPdfExtension && hasPdfMime;
};

const UPLOAD_DRAFT_STORAGE_KEY = 'mysign_upload_wizard_draft_v1';

interface UploadWizardDraft {
  activeStep: number;
  documentInfo: DocumentInfo;
  signers: Signer[];
  selectedOrganisationId: string | null;
}

const isSignerRowEmpty = (signer: Signer): boolean =>
  !signer.name.trim() &&
  !signer.surname.trim() &&
  !signer.email.trim() &&
  !(signer.phone?.trim()) &&
  !signer.fin.trim() &&
  !signer.tinCode.trim();

const scrollToFirstUploadError = (errorsMap: Record<string, string>, signers: Signer[]): void => {
  if (typeof document === 'undefined') {
    return;
  }
  const fieldOrder: string[] = ['organisation', 'signers'];
  signers.forEach((signer) => {
    fieldOrder.push(
      `${signer.id}-name`,
      `${signer.id}-surname`,
      `${signer.id}-email`,
      `${signer.id}-phone`,
      `${signer.id}-fin`,
    );
  });
  fieldOrder.push('title', 'expireDate', 'selectDocument', 'submit');
  const targetKey = fieldOrder.find((key) => errorsMap[key]);
  if (!targetKey) {
    return;
  }
  const el = document.querySelector(`[data-upload-field="${targetKey}"]`);
  if (!(el instanceof HTMLElement)) {
    return;
  }
  el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const focusable =
    el.matches('input,textarea,button')
      ? el
      : el.querySelector<HTMLElement>('input,textarea,button,[tabindex]');
  focusable?.focus({ preventScroll: true });
};

const readUploadErrorMessage = async (response: Response, fallback: string): Promise<string> => {
  try {
    const text = await response.text();
    if (!text.trim()) {
      return fallback;
    }
    try {
      const data = JSON.parse(text) as Record<string, unknown>;
      const candidates = [data.message, data.Message, data.detail, data.title, data.error];
      for (const candidate of candidates) {
        if (typeof candidate === 'string' && candidate.trim()) {
          return candidate.trim();
        }
      }
    } catch {
      if (text.length <= 400) {
        return text.trim();
      }
    }
  } catch {
    /* use fallback */
  }
  return fallback;
};

const clearUploadDraft = (): void => {
  try {
    sessionStorage.removeItem(UPLOAD_DRAFT_STORAGE_KEY);
  } catch {
    /* ignore */
  }
};

const UploadDocument: React.FC = () => {
  const { t } = useLanguage();
  const { token, user } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [documentInfo, setDocumentInfo] = useState<DocumentInfo>({
    title: '',
    description: '',
    expireDate: getDefaultExpireDate(),
    summary: '',
    operationId: '',
  });
  const [signers, setSigners] = useState<Signer[]>([createEmptySigner()]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [signerParties, setSignerParties] = useState<SignerParty[]>([]);
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [selectedOrganisation, setSelectedOrganisation] = useState<Organisation | null>(null);
  const [loadingOrganisations, setLoadingOrganisations] = useState<boolean>(true);
  const [organisationsError, setOrganisationsError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [documentDetailsOpen, setDocumentDetailsOpen] = useState(false);
  const [copiedSignerGuid, setCopiedSignerGuid] = useState<string | null>(null);
  const copiedTimeoutRef = useRef<number | null>(null);
  const uploadDraftHydratedRef = useRef(false);
  const validationAlertRef = useRef<HTMLDivElement | null>(null);
  const selectDocumentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return () => {
      if (copiedTimeoutRef.current !== null) {
        window.clearTimeout(copiedTimeoutRef.current);
      }
    };
  }, []);

  const copySigningLink = async (signerGUID: string) => {
    const url = `${process.env.REACT_APP_SELF_HOST}/document?sid=${signerGUID}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      return;
    }
    setCopiedSignerGuid(signerGUID);
    if (copiedTimeoutRef.current !== null) {
      window.clearTimeout(copiedTimeoutRef.current);
    }
    copiedTimeoutRef.current = window.setTimeout(() => {
      setCopiedSignerGuid((current) => (current === signerGUID ? null : current));
      copiedTimeoutRef.current = null;
    }, 2000);
  };

  // Fetch organisations when component mounts
  useEffect(() => {
    const fetchOrganisations = async () => {
      setLoadingOrganisations(true);
      setOrganisationsError('');
      try {

        const response = await fetch(`${process.env.REACT_APP_SIGNER_API_URL}/api/Organisation/GetAllOrganisations`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(t('failedToLoadOrganisations'));
        }

        const data = await response.json();
        const organisationsList = Array.isArray(data) ? data : [];
        setOrganisations(organisationsList);
        
        // If there's only one organisation, automatically select it and proceed
        if (organisationsList.length === 1) {
          setSelectedOrganisation(organisationsList[0]);
          setActiveStep(1);
        }
      } catch (error) {
        console.error('Error fetching organisations:', error);
        setOrganisationsError(t('failedToLoadOrganisations'));
      } finally {
        setLoadingOrganisations(false);
      }
    };

    fetchOrganisations();
  }, [token, t]);

  useEffect(() => {
    if (activeStep === 3) {
      return;
    }
    const stepName =
      activeStep === 0 ? 'organisation' : activeStep === 1 ? 'signers' : activeStep === 2 ? 'document' : String(activeStep);
    captureEvent('upload_step_viewed', { step: stepName });
  }, [activeStep]);

  useEffect(() => {
    if (loadingOrganisations || uploadDraftHydratedRef.current) {
      return;
    }
    uploadDraftHydratedRef.current = true;
    try {
      const raw = sessionStorage.getItem(UPLOAD_DRAFT_STORAGE_KEY);
      if (!raw) {
        return;
      }
      const draft = JSON.parse(raw) as UploadWizardDraft;
      if (draft.documentInfo) {
        setDocumentInfo(draft.documentInfo);
      }
      if (Array.isArray(draft.signers) && draft.signers.length > 0) {
        setSigners(draft.signers);
      }
      if (draft.selectedOrganisationId && organisations.length > 0) {
        const organisation = organisations.find((o) => o.id === draft.selectedOrganisationId);
        if (organisation) {
          setSelectedOrganisation(organisation);
        }
      }
      if (typeof draft.activeStep === 'number' && draft.activeStep >= 0 && draft.activeStep <= 2) {
        let step = draft.activeStep;
        if (organisations.length === 1 && step === 0) {
          step = 1;
        }
        setActiveStep(step);
      }
    } catch {
      clearUploadDraft();
    }
  }, [loadingOrganisations, organisations]);

  useEffect(() => {
    if (loadingOrganisations || activeStep === 3) {
      if (activeStep === 3) {
        clearUploadDraft();
      }
      return;
    }
    const draft: UploadWizardDraft = {
      activeStep,
      documentInfo,
      signers,
      selectedOrganisationId: selectedOrganisation?.id ?? null,
    };
    try {
      sessionStorage.setItem(UPLOAD_DRAFT_STORAGE_KEY, JSON.stringify(draft));
    } catch {
      /* ignore quota errors */
    }
  }, [activeStep, documentInfo, signers, selectedOrganisation, loadingOrganisations]);

  const hasUnsavedUploadProgress =
    activeStep < 3 &&
    (Boolean(documentInfo.title.trim()) ||
      Boolean(selectedFile) ||
      signers.some((signer) => !isSignerRowEmpty(signer)));

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!hasUnsavedUploadProgress) {
        return;
      }
      event.preventDefault();
      event.returnValue = t('uploadUnsavedLeaveWarning');
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasUnsavedUploadProgress, t]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (isSubmitting) {
      return;
    }
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    if (!isPdfFile(file)) {
      event.target.value = '';
      setSelectedFile(null);
      setErrors((prev) => ({ ...prev, submit: t('selectPdfFile') }));
      return;
    }
    setSelectedFile(file);
    const shouldFillTitle = !documentInfo.title.trim();
    if (shouldFillTitle) {
      const titleFromFile = file.name.replace(/\.pdf$/i, '').trim() || file.name;
      setDocumentInfo((prev) => ({ ...prev, title: titleFromFile }));
    }
    setErrors((prev) => {
      const next = { ...prev };
      delete next.submit;
      if (shouldFillTitle) {
        delete next.title;
      }
      return next;
    });
  };

  const handleDocumentInfoChange = (field: keyof DocumentInfo) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setDocumentInfo({ ...documentInfo, [field]: event.target.value });
  };

  const computeDocumentInfoErrors = (): { [key: string]: string } => {
    const newErrors: { [key: string]: string } = {};
    if (!documentInfo.title.trim()) newErrors.title = t('titleRequired');
    if (!documentInfo.expireDate) newErrors.expireDate = t('expireDateRequired');
    return newErrors;
  };

  const validateSigner = (signer: Signer) => {
    const newErrors: { [key: string]: string } = {};
    if (!signer.name.trim()) newErrors[`${signer.id}-name`] = t('nameRequired');
    if (!signer.surname.trim()) newErrors[`${signer.id}-surname`] = t('surnameRequired');
    if (!signer.email.trim()) newErrors[`${signer.id}-email`] = t('emailRequired');
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signer.email)) {
      newErrors[`${signer.id}-email`] = t('invalidEmailFormat');
    }
    // Phone number is optional; validate format only if provided
    if (signer.phone && !/^\+?[0-9]{7,15}$/.test(signer.phone)) {
      newErrors[`${signer.id}-phone`] = t('invalidPhoneLength');
    }
    // FIN and TIN are optional; validate only if provided
    if (signer.fin.trim() && !/^[A-Za-z0-9]{7}$/.test(signer.fin)) {
      newErrors[`${signer.id}-fin`] = t('finCodeLength');
    }
    return newErrors;
  };

  const applyValidationErrors = (nextErrors: Record<string, string>, step: number): void => {
    setErrors(nextErrors);
    const errorCount = Object.keys(nextErrors).length;
    if (errorCount === 0) {
      return;
    }
    captureEvent('upload_validation_failed', { step, error_count: errorCount });
    window.requestAnimationFrame(() => {
      validationAlertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      if (step === 2 && !selectedFile && nextErrors.submit) {
        if (!nextErrors.title && !nextErrors.expireDate) {
          selectDocumentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          scrollToFirstUploadError(nextErrors, signers);
        }
      } else {
        scrollToFirstUploadError(nextErrors, signers);
      }
    });
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      // Validate organisation selection
      if (!selectedOrganisation) {
        applyValidationErrors({ organisation: t('selectOrganisation') }, 0);
        return;
      }
      setErrors({});
      setActiveStep(1);
    } else if (activeStep === 1) {
      // Validate signers
      const allErrors: { [key: string]: string } = {};
      if (signers.length === 0) {
        allErrors.signers = t('addAtLeastOneSigner');
      }
      signers.forEach((signer) => {
        const signerErrors = validateSigner(signer);
        Object.assign(allErrors, signerErrors);
      });
      if (Object.keys(allErrors).length > 0) {
        applyValidationErrors(allErrors, 1);
        return;
      }
      setErrors({});
      setActiveStep(2);
    } else if (activeStep === 2) {
      const fieldErrors = computeDocumentInfoErrors();
      if (fieldErrors.expireDate) {
        setDocumentDetailsOpen(true);
      }
      const merged: { [key: string]: string } = { ...fieldErrors };
      if (!selectedFile) {
        merged.submit = t('selectFile');
      } else if (!isPdfFile(selectedFile)) {
        merged.submit = t('selectPdfFile');
      }
      if (Object.keys(fieldErrors).length > 0 || !selectedFile || !isPdfFile(selectedFile)) {
        applyValidationErrors(merged, 2);
        return;
      }

      setErrors({});
      // All validations passed, combine and submit
      try {
        setIsSubmitting(true);
        if (!selectedOrganisation) {
          setErrors({ submit: t('organisationNotSelected') });
          return;
        }

        const opId = documentInfo.operationId.trim();
        const fileData: UploadAndSubmitInfo = {
          fileName: selectedFile.name,
          title: documentInfo.title,
          description: documentInfo.description,
          expireDate: documentInfo.expireDate || '',
          summary: documentInfo.summary,
          ...(opId ? { operationId: opId } : {}),
          signers: signers.map(({ id: _clientId, ...signerPayload }) => signerPayload),  
          organisationId: selectedOrganisation.id ? parseInt(selectedOrganisation.id) : undefined
        };

        const formData = new FormData();
        formData.append('file', selectedFile);
        formData.append('fileData', JSON.stringify(fileData));

        const uploadUrl = `${process.env.REACT_APP_SIGNER_API_URL}/api/Document/UploadAndSubmitInfo`;
        const headers: HeadersInit = {
          'Authorization': `Bearer ${token}`,
        };

        const response = await fetch(uploadUrl, {
          method: 'POST',
          headers: headers,
          body: formData,
        });
        if (!response.ok) {
          const message = await readUploadErrorMessage(response, t('uploadFailed'));
          captureEvent('document_upload_failed', { http_status: response.status });
          setErrors({ submit: message });
          return;
        }
        const data = await response.json();
        const uploadResponse = data as UploadResponse;
        captureEvent('document_upload_completed', { signer_count: signers.length });
        clearUploadDraft();
        setSignerParties(uploadResponse.signParties ?? []);
        setActiveStep(3);
      } catch (error) {
        captureEvent('document_upload_failed', { http_status: 0 });
        setErrors({ submit: t('uploadFailed') });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const addSigner = () => {
    const newSigner: Signer = createEmptySigner();
    setSigners([...signers, newSigner]);
  };

  const userEmailNorm = user?.email ? normalizeEmail(user.email) : '';
  const selfAlreadyInSigners =
    Boolean(userEmailNorm) &&
    signers.some((s) => normalizeEmail(s.email) === userEmailNorm);

  const addMeAsSigner = () => {
    if (!user?.email?.trim() || selfAlreadyInSigners) {
      return;
    }
    const newSigner: Signer = {
      ...createEmptySigner(),
      name: user.name ?? '',
      surname: user.surname ?? '',
      email: user.email,
      phone: user.phone ?? '',
      fin: user.fin ?? '',
      signerType: SIGNER_TYPE_SIGNER,
    };
    if (signers.length === 1 && isSignerRowEmpty(signers[0])) {
      setSigners([newSigner]);
    } else {
      setSigners([...signers, newSigner]);
    }
    setErrors({});
  };

  const removeSigner = (id: string) => {
    setSigners(signers.filter(signer => signer.id !== id));
  };

  const updateSigner = <K extends keyof Signer>(id: string, field: K, value: Signer[K]) => {
    setSigners(signers.map((signer) =>
      signer.id === id ? { ...signer, [field]: value } : signer
    ));
  };

  const showOrganisationStep = organisations.length !== 1;
  const stepLabels = showOrganisationStep
    ? [t('stepSelectOrganisation'), t('step2'), t('document')]
    : [t('step2'), t('document')];
  const currentStepIndex = activeStep === 3
    ? stepLabels.length - 1
    : showOrganisationStep ? activeStep : activeStep - 1;

  const showValidationSummary = Object.keys(errors).length > 0 && activeStep !== 3;
  const highlightSelectDocument = activeStep === 2 && !selectedFile && Boolean(errors.submit);

  return (
    <div className={`space-y-3 md:space-y-4 ${isSubmitting ? 'pointer-events-none opacity-90' : ''}`}>
        <div className="flex flex-wrap gap-2">
          {stepLabels.map((step, idx) => (
            <div key={`${step}-${idx}`} className={`rounded-full px-3 py-1 text-sm ${currentStepIndex === idx ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
              {step}
            </div>
          ))}
        </div>

        {showValidationSummary && (
          <div ref={validationAlertRef}>
            <Alert variant="destructive">
              {errors.signers ?? t('uploadStepValidationIntro')}
            </Alert>
          </div>
        )}

        {activeStep === 0 && (
          <Card className="rounded-none">
            <CardHeader className="p-4 pb-3 md:p-6"><CardTitle>{t('selectStorageOrganisation')}</CardTitle></CardHeader>
            <CardContent className="space-y-3 p-4 pt-0 md:p-6">
            <p className="text-sm text-muted-foreground">{t('chooseStorageOrganisation')}</p>

            {loadingOrganisations && (
              <div className="flex justify-center py-4"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>
            )}

            {organisationsError && (
              <Alert variant="destructive">
                {organisationsError}
              </Alert>
            )}

            {!loadingOrganisations && !organisationsError && organisations.length === 0 && (
              <Alert variant="warning">
                {t('noOrganisationsAvailable')}
              </Alert>
            )}

            {!loadingOrganisations && organisations.length > 0 && (
              <>
                <RadioGroup
                  value={selectedOrganisation?.id || ''}
                  onValueChange={(value: string) => {
                    const organisation = organisations.find(p => p.id === value);
                    setSelectedOrganisation(organisation || null);
                    setErrors({ ...errors, organisation: '' });
                  }}
                >
                  <div className="space-y-2" data-upload-field="organisation">
                    {organisations.map((organisation) => (
                      <label
                        key={organisation.id}
                        className={`block cursor-pointer rounded-lg border p-3 ${selectedOrganisation?.id === organisation.id ? "border-primary bg-primary/5" : ""}`}
                        onClick={() => {
                          setSelectedOrganisation(organisation);
                          setErrors({ ...errors, organisation: '' });
                        }}
                      >
                          <div className="flex items-start gap-2">
                            <RadioGroupItem value={organisation.id} id={organisation.id} />
                            <div>
                                <p className="font-semibold">
                                    {organisation.name}
                                    {organisation.tin && ` (${organisation.tin})`}
                                </p>
                                {organisation.description && (
                                  <p className="mb-1 text-sm text-muted-foreground">
                                    {organisation.description}
                                  </p>
                                )}
                                <p className="text-xs text-muted-foreground">
                                  {t('endpoint')}: {organisation.endpoint}
                                </p>
                              </div>
                          </div>
                      </label>
                    ))}
                  </div>
                </RadioGroup>
                {errors.organisation && <p className="text-sm text-destructive">{errors.organisation}</p>}
              </>
            )}
            </CardContent>
          </Card>
        )}

        {activeStep === 1 && (
          
          <Card className="rounded-none">
            <CardContent className="space-y-3 p-4 md:p-6">

            {user?.email?.trim() && !selfAlreadyInSigners && (
              <Alert>
                <p className="text-sm text-muted-foreground">{t('addMeAsSignerPromo')}</p>
                <Button
                  type="button"
                  className="mt-3 w-full sm:w-auto"
                  onClick={addMeAsSigner}
                  disabled={isSubmitting}
                >
                  <UserPlus className="mr-2 h-4 w-4" />
                  {t('addMeAsSigner')}
                </Button>
              </Alert>
            )}

            {signers.length === 0 && (
              <Alert data-upload-field="signers">
                {t('addAtLeastOneSigner')}
              </Alert>
            )

            }

            <div>
              {signers.map((signer, index) => (
                <div key={signer.id} className={index > 0 ? "mt-4 border-t pt-4" : undefined}>
                    <div className="mb-2 flex items-center justify-between">
                      <h3 className="font-semibold">{t('signerLabel')} {index + 1}</h3>
                      <button
                        type="button"
                        onClick={() => removeSigner(signer.id)}
                        className="rounded p-1 text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-muted-foreground">{t('signerRole')}</Label>
                      <RadioGroup
                        value={String(signer.signerType)}
                        onValueChange={(v) =>
                          updateSigner(signer.id, 'signerType', Number(v) as Signer['signerType'])
                        }
                        className="flex flex-wrap gap-4"
                      >
                        <label className="flex cursor-pointer items-center gap-2 text-sm">
                          <RadioGroupItem value={String(SIGNER_TYPE_SIGNER)} id={`${signer.id}-role-signer`} />
                          <span>{t('roleSigner')}</span>
                        </label>
                        <label className="flex cursor-pointer items-center gap-2 text-sm">
                          <RadioGroupItem value={String(SIGNER_TYPE_VISA)} id={`${signer.id}-role-visa`} />
                          <span>{t('roleVisa')}</span>
                        </label>
                      </RadioGroup>
                    </div>
                    <div className="grid gap-2 md:grid-cols-2">
                      <div data-upload-field={`${signer.id}-name`}>
                        <Label>
                          {t('signerName')}
                          <span className="text-destructive" aria-hidden="true"> *</span>
                        </Label>
                        <Input value={signer.name} onChange={(e) => updateSigner(signer.id, 'name', e.target.value)} />
                        {errors[`${signer.id}-name`] && <p className="pt-1 text-xs text-destructive">{errors[`${signer.id}-name`]}</p>}
                      </div>
                      <div data-upload-field={`${signer.id}-surname`}>
                        <Label>
                          {t('signerSurname')}
                          <span className="text-destructive" aria-hidden="true"> *</span>
                        </Label>
                        <Input value={signer.surname} onChange={(e) => updateSigner(signer.id, 'surname', e.target.value)} />
                        {errors[`${signer.id}-surname`] && <p className="pt-1 text-xs text-destructive">{errors[`${signer.id}-surname`]}</p>}
                      </div>
                      <div data-upload-field={`${signer.id}-email`}>
                        <Label>
                          {t('signerEmail')}
                          <span className="text-destructive" aria-hidden="true"> *</span>
                        </Label>
                        <Input
                          type="email"
                          autoComplete="email"
                          value={signer.email}
                          onChange={(e) => updateSigner(signer.id, 'email', e.target.value)}
                        />
                        {errors[`${signer.id}-email`] && <p className="pt-1 text-xs text-destructive">{errors[`${signer.id}-email`]}</p>}
                      </div>
                    </div>
                    <div className="mt-3 space-y-2 rounded-lg border border-dashed bg-muted/30 p-3">
                      <p className="text-sm font-medium">{t('uploadSignerOptionalSection')}</p>
                      <p className="text-xs text-muted-foreground">{t('uploadFinOptionalHint')}</p>
                      <div className="grid gap-2 md:grid-cols-2">
                      <div data-upload-field={`${signer.id}-phone`}>
                        <Label>
                          {t('signerPhone')}
                          <span className="font-normal text-muted-foreground"> ({t('uploadOptionalSuffix')})</span>
                        </Label>
                        <Input value={signer.phone || ''} onChange={(e) => updateSigner(signer.id, 'phone', e.target.value)} />
                        {errors[`${signer.id}-phone`] && <p className="pt-1 text-xs text-destructive">{errors[`${signer.id}-phone`]}</p>}
                      </div>
                      <div data-upload-field={`${signer.id}-fin`}>
                        <Label>
                          {t('finCode')}
                          <span className="font-normal text-muted-foreground"> ({t('uploadOptionalSuffix')})</span>
                        </Label>
                        <Input maxLength={7} value={signer.fin} onChange={(e) => updateSigner(signer.id, 'fin', e.target.value)} />
                        {errors[`${signer.id}-fin`] && <p className="pt-1 text-xs text-destructive">{errors[`${signer.id}-fin`]}</p>}
                      </div>
                      </div>
                    </div>
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="secondary"
              className="w-full"
              onClick={addSigner}
              disabled={isSubmitting}
            >
              <Plus className="mr-2 h-4 w-4" />
              {t('addSigner')}
            </Button>
            </CardContent>
          </Card>
        )}

        {activeStep === 2 && (
          <Card className="rounded-none">
            <CardContent className="space-y-3 p-4 md:p-6">
              <div data-upload-field="title">
                <Label>
                  {t('documentTitle')}
                  <span className="text-destructive" aria-hidden="true"> *</span>
                </Label>
                <Input value={documentInfo.title} onChange={handleDocumentInfoChange('title')} />
                {errors.title && <p className="text-xs text-destructive">{errors.title}</p>}
              </div>
              <button
                type="button"
                className="flex w-full items-center justify-between gap-2 text-left text-sm font-medium text-primary"
                aria-expanded={documentDetailsOpen}
                aria-controls="upload-document-details"
                onClick={() => setDocumentDetailsOpen((open) => !open)}
              >
                <span>{documentDetailsOpen ? t('hideDocumentDetails') : t('moreDocumentDetails')}</span>
                <ChevronDown
                  className={cn('h-4 w-4 shrink-0 transition-transform', documentDetailsOpen && 'rotate-180')}
                  aria-hidden
                />
              </button>
              {documentDetailsOpen && (
                <div id="upload-document-details" className="space-y-3">
                  <div>
                    <Label>{t('uploadOperationIdLabel')}</Label>
                    <Input
                      value={documentInfo.operationId}
                      onChange={handleDocumentInfoChange('operationId')}
                      placeholder={t('uploadOperationIdPlaceholder')}
                      autoComplete="off"
                    />
                    <p className="mt-1 text-xs text-muted-foreground">{t('uploadOperationIdHint')}</p>
                  </div>
                  <div>
                    <Label>{t('description')}</Label>
                    <Textarea rows={3} value={documentInfo.description} onChange={handleDocumentInfoChange('description')} />
                  </div>
                  <div data-upload-field="expireDate">
                    <Label>{t('expireDate')}</Label>
                    <Input type="date" value={documentInfo.expireDate} onChange={handleDocumentInfoChange('expireDate')} />
                    {errors.expireDate && <p className="text-xs text-destructive">{errors.expireDate}</p>}
                  </div>
                  <div>
                    <Label>{t('summary')}</Label>
                    <Textarea rows={4} value={documentInfo.summary} onChange={handleDocumentInfoChange('summary')} />
                  </div>
                </div>
              )}

              <div className="border-t pt-4" data-upload-field="submit">
                <div
                  ref={selectDocumentRef}
                  data-upload-field="selectDocument"
                  className={cn(
                    'flex flex-col gap-2 rounded-lg sm:flex-row sm:items-center',
                    highlightSelectDocument && 'ring-2 ring-destructive ring-offset-2 ring-offset-background',
                  )}
                >
                  <Button
                    asChild
                    disabled={isSubmitting}
                    variant={highlightSelectDocument ? 'default' : 'secondary'}
                    className={cn(highlightSelectDocument && 'shadow-sm')}
                  >
                    <label className={`cursor-pointer ${isSubmitting ? 'pointer-events-none opacity-60' : ''}`}>
                      <UploadCloud className="mr-2 h-4 w-4" />
                      {t('selectDocument')}
                      <input
                        className="sr-only"
                        type="file"
                        onChange={handleFileSelect}
                        accept=".pdf,application/pdf"
                        disabled={isSubmitting}
                      />
                    </label>
                  </Button>
                  {selectedFile && (
                    <p className="text-sm">
                      <span className="text-muted-foreground">{t('selectedFile')}:</span> {selectedFile.name}
                    </p>
                  )}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{t('selectDocumentHint')}</p>
                {errors.submit && <p className="pt-2 text-xs text-destructive">{errors.submit}</p>}
              </div>
            </CardContent>
          </Card>
        )}

        {activeStep === 3 && (
          <Card className="rounded-none">
            <CardContent className="space-y-3 p-4 text-center md:p-6">
            <CheckCircle className="mx-auto h-12 w-12 text-green-600" />
            <h2 className="text-2xl font-semibold">
              {t('documentSuccessfullyUploaded')}
            </h2>
            <p className="text-muted-foreground">
              {t('thankYouMessage')}
            </p>
            <div className="space-y-2 text-left">
              {signerParties.length > 0 && (
                <div>
                  <h3 className="pb-1 font-semibold">
                    {t('signers')}:
                  </h3>
                  <div className="space-y-2">
                    {signerParties.map((signer) => (
                      <div key={signer.signerGUID} className="min-w-0 rounded border p-3 text-left">
                        <div className="min-w-0">
                          <p className="break-words font-semibold">
                            {signer.name} {signer.surname}
                          </p>
                          {signer.signerType === SIGNER_TYPE_VISA && (
                            <p className="text-xs font-medium text-primary">{t('roleVisa')}</p>
                          )}
                          <p className="break-all text-sm text-muted-foreground">
                            {t('email')}: {signer.email}
                          </p>
                          {signer.phone && (
                            <p className="break-all text-sm text-muted-foreground">
                              {t('phone')}: {signer.phone}
                            </p>
                          )}
                          {signer.tin && (
                            <p className="break-all text-sm text-muted-foreground">
                              {t('tinCode')}: {signer.tin}
                            </p>
                          )}
                          {signer.fin && (
                            <p className="break-all text-sm text-muted-foreground">
                              {t('finCode')}: {signer.fin}
                            </p>
                          )}
                        </div>
                        <div className="mt-2 flex items-center justify-between gap-2">
                          <a
                            href={`${process.env.REACT_APP_SELF_HOST}/document?sid=${signer.signerGUID}`}
                            target="_blank"
                            rel="noreferrer"
                            className="min-w-0 break-words text-sm text-primary hover:underline"
                          >
                            {t('signingLink')}
                          </a>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="shrink-0"
                            aria-label={t('signingLink')}
                            onClick={() => copySigningLink(signer.signerGUID)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        </div>
                        {copiedSignerGuid === signer.signerGUID && (
                          <p className="text-sm text-muted-foreground" aria-live="polite">
                            {t('signingLinkCopied')}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:justify-center">
              <Button asChild>
                <Link to="/documents">{t('goToDocumentList')}</Link>
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  clearUploadDraft();
                  setActiveStep(organisations.length === 1 ? 1 : 0);
                  setSelectedFile(null);
                  setDocumentInfo({
                    title: '',
                    description: '',
                    expireDate: getDefaultExpireDate(),
                    summary: '',
                    operationId: '',
                  });
                  setSigners([createEmptySigner()]);
                  setErrors({});
                  setSignerParties([]);
                  setDocumentDetailsOpen(false);
                  if (organisations.length !== 1) setSelectedOrganisation(null);
                }}
              >
                <UploadCloud className="h-4 w-4" />
                {t('uploadDocument')}
              </Button>
            </div>
            </CardContent>
          </Card>
        )}

        {isSubmitting && (
          <Alert>
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{t('uploadInProgress')}</span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{t('uploadInProgressHint')}</p>
          </Alert>
        )}

        <div className="sticky bottom-0 z-10 -mx-1 border-t bg-background/95 p-3 backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:-mx-0">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={isSubmitting || activeStep === 0 || (organisations.length === 1 && activeStep === 1)}
          >
            <ChevronLeft className="h-4 w-4" />
            {t('back')}
          </Button>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {activeStep !== 3 && (
              <Button
                onClick={handleNext}
                disabled={loadingOrganisations || isSubmitting}
              >
                {activeStep === 2 ? (
                  isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
                {activeStep === 2 ? (isSubmitting ? t('uploading') : t('submit')) : t('next')}
              </Button>
            )}
          </div>
        </div>
        </div>
    </div>
  );
};

export default UploadDocument;
